import { LegalModal, PolicyType, useLegalPolicies } from '@practium/features/policy';
import { FormField, FormInput, FormSelect, LoadingState } from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { STATUS_OPTIONS } from '../../../constants/users.constants';
import { useSystemUserForm } from '../../../hooks/useSystemUserForm';
import type { SystemUserFormData, UserPageMode } from '../../../types';
import { applyPhoneMask } from '../../../utils/masks';
import styles from '../PersonForm.module.css';

export interface UserFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface UserFormProps {
	mode: UserPageMode;
	initialData?: SystemUserFormData;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
}

export const UserForm = forwardRef<UserFormRef, UserFormProps>(function UserForm(
	{ mode, initialData, onSubmit, onLoadingChange },
	ref
) {
	const { form, errors, isLoading, planOptions, isEditMode, set, handleSubmit, isDirty } =
		useSystemUserForm({
			initialData,
			onSubmit,
		});
	const { activeLegalLinks, isLoading: isPoliciesLoading } = useLegalPolicies();
	const [activeModal, setActiveModal] = useState<PolicyType | null>(null);

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	const isView = mode === 'view';

	if (isView) {
		return (
			<div className={styles.viewGrid}>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Nome</span>
					<span className={styles.viewValue}>{form.name || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Sobrenome</span>
					<span className={styles.viewValue}>{form.surName || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>E-mail</span>
					<span className={styles.viewValue}>{form.email || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Telefone (Whatsapp)</span>
					<span className={styles.viewValue}>{form.phoneNumber || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Pacote</span>
					<span className={styles.viewValue}>
						{planOptions.find((p) => p.value === form.planId)?.label || '—'}
					</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Status</span>
					<span className={styles.viewValue}>
						{form.status === 'true' ? 'Ativo' : form.status === 'false' ? 'Inativo' : '—'}
					</span>
				</div>
			</div>
		);
	}

	if (isPoliciesLoading) {
		return <LoadingState fullScreen />;
	}

	return (
		<>
			<div className={styles.formGrid}>
				<FormField label="Nome" required error={errors.name}>
					<FormInput
						value={form.name}
						onChange={(e) => set('name', e.target.value)}
						placeholder="Ex.: Jefferson"
						hasError={!!errors.name}
					/>
				</FormField>
				<FormField label="Sobrenome" required error={errors.surName}>
					<FormInput
						value={form.surName}
						onChange={(e) => set('surName', e.target.value)}
						placeholder="Ex.: Silva"
						hasError={!!errors.surName}
					/>
				</FormField>
				<FormField label="E-mail" required error={errors.email}>
					<FormInput
						value={form.email}
						onChange={(e) => set('email', e.target.value)}
						placeholder="Ex.: jefferson@gmail.com"
						type="email"
						hasError={!!errors.email}
					/>
				</FormField>
				<FormField label="Telefone (Whatsapp)" error={errors.phoneNumber}>
					<FormInput
						value={form.phoneNumber}
						onChange={(e) => set('phoneNumber', applyPhoneMask(e.target.value))}
						placeholder="Ex.: (xx) xxxxx-xxxx"
						hasError={!!errors.phoneNumber}
					/>
				</FormField>
				<FormField label="Pacote" required error={errors.planId}>
					<FormSelect
						value={form.planId}
						onChange={(v) => set('planId', v)}
						options={planOptions}
						placeholder="Selecione o Pacote"
						hasError={!!errors.planId}
					/>
				</FormField>
				<FormField label="Status" required error={errors.status}>
					<FormSelect
						value={form.status}
						onChange={(v) => set('status', v)}
						options={STATUS_OPTIONS}
						placeholder="Selecione o Status"
						hasError={!!errors.status}
					/>
				</FormField>
				{!isEditMode && (
					<p className={styles.infoText}>
						Uma senha temporária será enviada para o e-mail informado.
					</p>
				)}
				{!isEditMode && (
					<div className={`${styles.checkboxRow} ${styles.fullWidth}`}>
						<input
							type="checkbox"
							id="acceptedTerms"
							className={styles.checkboxInput}
							checked={form.acceptedTerms}
							onChange={(e) => set('acceptedTerms', e.target.checked)}
						/>
						<div className={styles.termsContent}>
							<label htmlFor="acceptedTerms" className={styles.termsTitle}>
								Declaro que li e aceito os Termos de Uso e Políticas de Privacidade
							</label>
							<div className={styles.termsLinks}>
								<button
									type="button"
									className={styles.termsLink}
									onClick={() => setActiveModal('terms_of_use')}
								>
									Termos de Uso
								</button>
								<span className={styles.termsSeparator}>|</span>
								<button
									type="button"
									className={styles.termsLink}
									onClick={() => setActiveModal('privacy_policy')}
								>
									Política de Privacidade
								</button>
							</div>
						</div>
					</div>
				)}
			</div>

			{activeLegalLinks.map((modal) => (
				<LegalModal
					key={modal.key}
					open={activeModal === modal.key}
					onClose={() => setActiveModal(null)}
					policy={modal.policy}
				/>
			))}
		</>
	);
});
